import { CsvColumnMapping, ImportRow } from "./import-types";
import { IssuePriority, IssueType } from "./types";

// ── CSV (RFC 4180) ────────────────────────────────────────────────────────
// Quoted fields may contain commas/newlines; "" is an escaped quote. Returns a grid of cells.
export function parseCsv(input: string): string[][] {
  let text: string = input.charCodeAt(0) === 0xfeff ? input.slice(1) : input; // strip BOM
  const rows: string[][] = [];
  let row: string[] = [];
  let field: string = "";
  let inQuotes: boolean = false;
  let i: number = 0;
  while (i < text.length) {
    const c: string = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += c;
      i += 1;
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      i += 1;
    } else if (c === ",") {
      row.push(field);
      field = "";
      i += 1;
    } else if (c === "\r") {
      i += 1;
    } else if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      i += 1;
    } else {
      field += c;
      i += 1;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  // Drop fully-empty trailing rows.
  return rows.filter((r: string[]) => r.some((cell: string) => cell.trim() !== ""));
}

function findHeader(headers: string[], names: string[]): number | null {
  const lower: string[] = headers.map((h: string) => h.trim().toLowerCase());
  for (const name of names) {
    const idx: number = lower.indexOf(name);
    if (idx >= 0) return idx;
  }
  return null;
}

// Pre-fill a column mapping from the headers. The alias lists are a verified superset across sources:
//   Jira    — "Issue key", "Issue Type", "Summary", "Status", "Story Points"/"Story point estimate",
//             "Epic Link" (legacy) or "Parent" (modern Cloud — same column for a story's epic and a
//             sub-task's parent; the backend disambiguates by the resolved target's type), repeated "Labels".
//   Linear  — "ID", "Title", "Status", "Estimate", "Parent issue", "Labels".
//   Asana   — "Task ID", "Name", "Notes", "Tags", "Parent task" (holds the parent's NAME), "Creation Date".
//             Asana does NOT export sections, so status often won't auto-map — the user picks it.
//   Trello  — parsed from JSON, not here. Basecamp/CSV — generic; the user adjusts whatever doesn't match.
export function detectCsvMapping(headers: string[]): CsvColumnMapping {
  const lower: string[] = headers.map((h: string) => h.trim().toLowerCase());
  const labelCols: number[] = lower
    .map((h: string, i: number) => (h === "labels" || h === "label" || h === "tags" ? i : -1)) // Asana uses "Tags"
    .filter((i: number) => i >= 0);
  return {
    summary: findHeader(headers, ["summary", "title", "name", "task name", "content"]),
    description: findHeader(headers, ["description", "desc", "notes", "body"]),
    type: findHeader(headers, ["issue type", "type", "work item type"]),
    status: findHeader(headers, ["status", "state", "section/column", "section", "list", "column"]),
    priority: findHeader(headers, ["priority"]),
    assignee: findHeader(headers, ["assignee", "assigned to", "owner", "created by"]),
    labels: labelCols,
    storyPoints: findHeader(headers, ["story points", "story point estimate", "estimate", "points"]),
    epicKey: findHeader(headers, ["epic link", "epic", "epic key"]),
    parentKey: findHeader(headers, ["parent", "parent issue", "parent key", "sub-task of", "parent task"]),
    externalKey: findHeader(headers, ["issue key", "key", "issue id", "id", "task id", "identifier"]),
    createdAt: findHeader(headers, ["created", "created at", "created date", "creation date"])
  };
}

function cell(row: string[], index: number | null): string {
  if (index === null || index < 0 || index >= row.length) return "";
  return (row[index] ?? "").trim();
}

function parsePoints(raw: string): number | null {
  if (!raw) return null;
  const n: number = Number(raw.replace(",", "."));
  return Number.isFinite(n) ? Math.round(n) : null;
}

// Apply a column mapping to the CSV data rows → normalized ImportRow[].
export function rowsFromCsv(grid: string[][], mapping: CsvColumnMapping): ImportRow[] {
  const data: string[][] = grid.slice(1);
  return data
    .map((row: string[]): ImportRow => {
      const labels: string[] = mapping.labels
        .flatMap((i: number) => cell(row, i).split(/[,;]/))
        .map((l: string) => l.trim())
        .filter((l: string) => l.length > 0);
      return {
        externalKey: cell(row, mapping.externalKey),
        summary: cell(row, mapping.summary),
        description: cell(row, mapping.description),
        type: cell(row, mapping.type),
        status: cell(row, mapping.status),
        priority: cell(row, mapping.priority),
        assignee: cell(row, mapping.assignee),
        labels,
        storyPoints: parsePoints(cell(row, mapping.storyPoints)),
        epicKey: cell(row, mapping.epicKey),
        parentKey: cell(row, mapping.parentKey),
        createdAt: cell(row, mapping.createdAt)
      };
    })
    .filter((r: ImportRow) => r.summary.length > 0);
}

// ── Trello board JSON ──────────────────────────────────────────────────────
interface TrelloCard {
  id: string;
  name: string;
  desc: string;
  idList: string;
  idLabels: string[];
  idMembers: string[];
  closed: boolean;
  dateLastActivity?: string;
}
interface TrelloNamed {
  id: string;
  name: string;
}
interface TrelloMember {
  id: string;
  fullName: string;
  username: string;
}
interface TrelloBoard {
  cards?: TrelloCard[];
  lists?: TrelloNamed[];
  labels?: TrelloNamed[];
  members?: TrelloMember[];
}

// Trello's board export: open cards → issues, list name → status, labels, first member → assignee.
export function parseTrello(jsonText: string): ImportRow[] {
  const board: TrelloBoard = JSON.parse(jsonText) as TrelloBoard;
  const listName: Map<string, string> = new Map((board.lists ?? []).map((l: TrelloNamed) => [l.id, l.name]));
  const labelName: Map<string, string> = new Map((board.labels ?? []).map((l: TrelloNamed) => [l.id, l.name]));
  const memberName: Map<string, string> = new Map(
    (board.members ?? []).map((m: TrelloMember) => [m.id, m.fullName || m.username])
  );
  return (board.cards ?? [])
    .filter((card: TrelloCard) => !card.closed)
    .map((card: TrelloCard): ImportRow => ({
      externalKey: card.id,
      summary: card.name,
      description: card.desc ?? "",
      type: "task",
      status: listName.get(card.idList) ?? "",
      priority: "",
      assignee: card.idMembers.map((id: string) => memberName.get(id) ?? "").find((n: string) => n.length > 0) ?? "",
      labels: card.idLabels.map((id: string) => labelName.get(id) ?? "").filter((n: string) => n.length > 0),
      storyPoints: null,
      epicKey: "",
      parentKey: "",
      createdAt: card.dateLastActivity ?? ""
    }))
    .filter((r: ImportRow) => r.summary.length > 0);
}

// ── Value normalization (raw source label → our enum) ──────────────────────
export function normalizeType(raw: string): IssueType {
  const t: string = raw.trim().toLowerCase();
  if (t === "epic") return "epic";
  if (t === "bug" || t === "defect") return "bug";
  if (t === "story" || t === "feature" || t === "user story") return "feature";
  if (t === "chore" || t === "task-chore") return "chore";
  return "task"; // task, sub-task, and anything unknown
}

export function normalizePriority(raw: string): IssuePriority {
  const p: string = raw.trim().toLowerCase();
  if (p === "urgent" || p === "highest" || p === "1" || p === "critical" || p === "blocker") return "urgent";
  if (p === "high" || p === "2") return "high";
  if (p === "medium" || p === "normal" || p === "3") return "medium";
  if (p === "low" || p === "4") return "low";
  if (p === "lowest" || p === "none" || p === "no priority" || p === "5" || p === "0" || p === "") return "none";
  return "medium";
}
